import { useEffect, useMemo } from "react";
import { keyScale, keyTonicPc, spellInKey } from "../../theory/key";
import { octaveOf } from "../../theory/notes";
import { DRUM_LANES } from "../../sequencer/patterns";
import { Keyboard } from "../components/Keyboard";
import { Pads } from "../components/Pads";
import { SynthPanel } from "../components/SynthPanel";
import { Panel, Toggle } from "../components/controls";
import { useLive, useProject, useServices } from "../context";
import { KEY_LABEL, KEY_TO_SEMITONE, useComputerKeyboard } from "../hooks/useComputerKeyboard";

export function Playground() {
  const { controller } = useServices();
  const key = useProject((p) => p.song.key);
  const octave = useProject((p) => p.playground.octave);
  const keysTrack = useProject((p) => p.playground.keyboardTrackId);
  const liveTranspose = useProject((p) => p.song.transposeLiveInput);
  const transpose = useProject((p) => p.song.transposeSemitones);
  const heldArr = useLive((s) => s.heldNotes);
  const held = useMemo(() => new Set(heldArr.map((n) => n - (liveTranspose ? transpose : 0))), [heldArr, liveTranspose, transpose]);
  const scale = useMemo(() => new Set(keyScale(key).pitchClasses), [key]);
  const startNote = (octave + 1) * 12; // C of the selected octave

  const setOctave = (o: number) => controller.updateProject((p) => ({ ...p, playground: { ...p.playground, octave: Math.max(0, Math.min(7, o)) } }));
  useComputerKeyboard(startNote, (n) => controller.noteOn(n), (n) => controller.noteOff(n), (d) => setOctave(octave + d));

  // Number keys 1–5 trigger pads.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey || (el?.tagName && ["INPUT", "SELECT", "TEXTAREA"].includes(el.tagName))) return;
      const i = ["Digit1", "Digit2", "Digit3", "Digit4"].indexOf(e.code);
      if (i >= 0) controller.triggerDrum(DRUM_LANES[i]!);
      if (e.code === "Digit5") controller.triggerSample();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [controller]);

  const hints = useMemo(() => new Map(Object.entries(KEY_TO_SEMITONE).map(([code, s]) => [startNote + s, KEY_LABEL[code]!])), [startNote]);

  return (
    <div className="workspace-grid playground">
      <Panel
        title="Keyboard" className="span-all"
        actions={
          <>
            <button type="button" onClick={() => setOctave(octave - 1)} disabled={octave <= 0} aria-label="Octave down (Z)">Oct −</button>
            <span className="readout" aria-live="polite">C{octave}–C{octave + 2}</span>
            <button type="button" onClick={() => setOctave(octave + 1)} disabled={octave >= 7} aria-label="Octave up (X)">Oct +</button>
            <Toggle pressed={liveTranspose} onChange={(v) => controller.updateProject((p) => ({ ...p, song: { ...p.song, transposeLiveInput: v } }))} title="Apply song transposition to keyboard and MIDI input">
              Transpose input
            </Toggle>
          </>
        }
      >
        <Keyboard
          startNote={startNote} octaves={2} highlight={scale} tonicPc={keyTonicPc(key)} held={held}
          nameOf={(n) => `${spellInKey(n, key)}${octaveOf(n)}`} keyHints={hints}
          onNoteOn={(n) => controller.noteOn(n)} onNoteOff={(n) => controller.noteOff(n)}
        />
        <p className="hint">Play with the mouse, touch, or computer keys A–; (Z/X change octave). Highlighted keys belong to {key.tonic} {key.mode}. MIDI keyboards: enable in My Gear.</p>
      </Panel>
      <SynthPanel trackId={keysTrack} title="Keys sound" />
      <Pads />
    </div>
  );
}
